import { formatDay, formatTime, TODAY } from './format.ts'

export const toIso = (date: string, time: string): string => {
  const wall = `${date}T${time || '18:00'}:00`
  let instant = Date.parse(`${wall}Z`)
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const parts = kyivParts(new Date(instant).toISOString())
    const rendered = Date.parse(`${parts.date}T${parts.time}:00Z`)
    instant += Date.parse(`${wall}Z`) - rendered
  }
  return new Date(instant).toISOString()
}

const kyivParts = (iso: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  }
}

export const datePart = (iso: string | null): string => (iso ? kyivParts(iso).date : '')
export const timePart = (iso: string | null): string => (iso ? kyivParts(iso).time : '')

const addDays = (date: string, days: number): string => {
  const value = new Date(`${date}T12:00:00Z`)
  value.setUTCDate(value.getUTCDate() + days)
  return value.toISOString().slice(0, 10)
}

const nextMonday = (date: string): string => {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay()
  return addDays(date, (8 - weekday) % 7 || 7)
}

export const QUICK_DUE = [
  { label: 'Сьогодні', iso: () => toIso(TODAY, '18:00') },
  { label: 'Завтра', iso: () => toIso(addDays(TODAY, 1), '10:00') },
  { label: 'Наступного тижня', iso: () => toIso(nextMonday(TODAY), '10:00') },
] as const

export type DueGroup = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none'

export const DUE_GROUP_LABELS: Record<DueGroup, string> = {
  overdue: 'Прострочено',
  today: 'Сьогодні',
  tomorrow: 'Завтра',
  week: 'Найближчі 7 днів',
  later: 'Пізніше',
  none: 'Без терміну',
}

export const dueGroup = (iso: string | null): DueGroup => {
  if (!iso) return 'none'
  const date = datePart(iso)
  if (date < TODAY) return 'overdue'
  if (date === TODAY) return 'today'
  if (date === addDays(TODAY, 1)) return 'tomorrow'
  if (date <= addDays(TODAY, 7)) return 'week'
  return 'later'
}

/** "Сьогодні, 15:00", "Завтра, 10:00", "12 жовт., 10:00". */
/** «Сьогодні, 18:00»; with `dateOnly` (a deadline without a time) just «Сьогодні». */
export const dueLabel = (iso: string, dateOnly = false): string => {
  const group = dueGroup(iso)
  const day = group === 'today' ? 'Сьогодні' : group === 'tomorrow' ? 'Завтра' : formatDay(iso)
  return dateOnly ? day : `${day}, ${formatTime(iso)}`
}

export const REMINDERS = [
  { key: 'none', label: 'Без нагадування', minutes: null },
  { key: 'at', label: 'У момент терміну', minutes: 0 },
  { key: '15m', label: 'За 15 хвилин', minutes: 15 },
  { key: '1h', label: 'За 1 годину', minutes: 60 },
  { key: '1d', label: 'За 1 день', minutes: 1440 },
] as const

export type ReminderKey = (typeof REMINDERS)[number]['key']

export const reminderAt = (dueAt: string | null, key: ReminderKey): string | null => {
  const option = REMINDERS.find((candidate) => candidate.key === key)
  if (!dueAt || !option || option.minutes === null) return null
  return new Date(Date.parse(dueAt) - option.minutes * 60_000).toISOString()
}

export const reminderKey = (dueAt: string | null, at: string | null): ReminderKey => {
  if (!dueAt || !at) return 'none'
  const minutes = Math.round((Date.parse(dueAt) - Date.parse(at)) / 60_000)
  return REMINDERS.find((option) => option.minutes === minutes)?.key ?? '1h'
}
